import React, { memo, useCallback, useMemo } from 'react'

import { Icon, RotatingSpinner, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { string, func } from 'prop-types'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setOpenedModel } from 'Pods/ThreeDStudio/Store/actions/ui'

import { NOOP } from 'Utils/stableDefaults'

const iconSize = { height: '24px', width: '24px' }

function LoadingModelCard({ content = '', onNavigate = NOOP }) {
  const navigate = useNavigate()
  const location = useLocation()
  const runId = useThreeDStudioStore(state => state.generation?.runId)
  const lastGenerationSettings = useThreeDStudioStore(state => state.generation?.lastGenerationSettings)
  const generatingId = lastGenerationSettings?.id

  const hasGeneratingId = generatingId != null
  // Match the generating card by the pathname it navigates to (slug, or the
  // <title>-<id> fallback for runs without a persisted slug), not a numeric id.
  const generatingPathname = useMemo(() => (
    hasGeneratingId
      ? buildSharedRunPathname({
        slug: lastGenerationSettings.slug,
        id: lastGenerationSettings.id,
        title: lastGenerationSettings.title ?? lastGenerationSettings.prompt,
      })
      : null
  ), [hasGeneratingId, lastGenerationSettings])
  const isActiveRunMatch = generatingPathname != null && location?.pathname === generatingPathname
  const isAiPageMatch = location?.pathname === '/ai' && String(runId) === String(generatingId)
  const isActive = hasGeneratingId && (isActiveRunMatch || isAiPageMatch)

  const handleClick = useCallback(() => {
    setOpenedModel(lastGenerationSettings ?? {})

    if (lastGenerationSettings?.id == null) {
      return
    }
    if (location?.pathname !== generatingPathname) {
      navigate(generatingPathname)
    }
    onNavigate()
  }, [navigate, location, lastGenerationSettings, generatingPathname, onNavigate])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }, [handleClick])

  return (
    <div
      className={classnames('model-card', isActive && 'model-card--active')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="model-card__generating">
        <Icon svgComponent={RotatingSpinner} size={iconSize} color="primary" notSpaced />
        {content && <span className="model-card__generating-text">{content}</span>}
      </div>
    </div>
  )
}

LoadingModelCard.propTypes = {
  content: string,
  onNavigate: func,
}

export default memo(LoadingModelCard)
